import { FieldValues, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

/**
 * Validation messages are translation keys. Translates every `message` in a react-hook-form error tree
 * (including terms nested in arrays), leaving the tree's shape - and the DOM `ref`s in it - untouched.
 */
export const translateFormErrors = <T>(errors: T, t: (key: string) => string): T => {
  if (Array.isArray(errors)) return errors.map((error) => translateFormErrors(error, t)) as T;
  if (errors && typeof errors === 'object' && Object.getPrototypeOf(errors) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(errors).map(([key, value]) => [
        key,
        key === 'message' && typeof value === 'string' ? t(value) : translateFormErrors(value, t),
      ])
    ) as T;
  }
  return errors;
};

/** A zod resolver whose messages (translation keys) come back translated. */
export const useTranslatedResolver = <T extends FieldValues>(schema: z.ZodTypeAny): Resolver<T> => {
  const { t } = useTranslation();
  return async (data, context, options) => {
    const result = await (zodResolver(schema) as unknown as Resolver<T>)(data, context, options);
    if (result.errors) result.errors = translateFormErrors(result.errors, t);
    return result;
  };
};
